"use client"

import { useEffect, useMemo, useRef } from 'react'
import { ArrowRight, ClipboardList } from 'lucide-react'

import { getAngiAffiliateUrl } from '@/data/angi-affiliate'
import {
  SERVICE_QUOTE_FUNNEL_BY_TYPE,
  getServiceQuoteFunnelForContent,
  type ServiceLeadServiceType,
} from '@/data/service-quote-funnels'
import { trackEvent } from '@/lib/analytics'

interface ServiceQuoteCTAProps {
  variant?: string
  slug?: string
  tags?: string[]
  /** CJ sid and PostHog `source_placement`. Required: it used to default to
   *  `blog_inline_service_quote`, which is now a retired placement, so a caller
   *  that omitted it would bury fresh clicks in a dead surface's history. */
  sourcePlacement: string
  sourcePage?: string
}

function isServiceType(value: string | undefined): value is ServiceLeadServiceType {
  return Boolean(value && value in SERVICE_QUOTE_FUNNEL_BY_TYPE)
}

function inferSourcePage(slug: string | undefined, sourcePlacement: string): string | undefined {
  if (!slug) return undefined
  if (sourcePlacement.startsWith('tool_')) return `/tools/${slug}`
  if (sourcePlacement.startsWith('city_')) return `/lawn-care/${slug}`
  return `/blog/${slug}`
}

export function ServiceQuoteCTA({
  variant,
  slug,
  tags = [],
  sourcePlacement,
  sourcePage: sourcePageProp,
}: ServiceQuoteCTAProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const viewedRef = useRef(false)
  const funnel = useMemo(() => {
    if (isServiceType(variant)) return SERVICE_QUOTE_FUNNEL_BY_TYPE[variant]
    return getServiceQuoteFunnelForContent(slug, undefined, tags) ?? SERVICE_QUOTE_FUNNEL_BY_TYPE.lawn_care
  }, [variant, slug, tags])

  const sourcePage = sourcePageProp ?? inferSourcePage(slug, sourcePlacement)
  // Direct Angi affiliate handoff (2026-07-18 pivot): the CTA links out to
  // Angi's request funnel instead of our paused first-party lead form. The
  // placement rides along as CJ's sid so commission reports split by surface.
  const href = getAngiAffiliateUrl(funnel.serviceType, sourcePlacement, slug)
  const props = {
    service_type: funnel.serviceType,
    source_page: sourcePage,
    source_placement: sourcePlacement,
  }

  useEffect(() => {
    const node = containerRef.current
    if (!node || viewedRef.current) return
    if (typeof IntersectionObserver === 'undefined') {
      viewedRef.current = true
      trackEvent('service_lead_cta_viewed', props)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (viewedRef.current) return
        const entry = entries[0]
        if (!entry?.isIntersecting) return
        viewedRef.current = true
        trackEvent('service_lead_cta_viewed', props)
        observer.disconnect()
      },
      { threshold: 0.35 }
    )

    observer.observe(node)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funnel.serviceType, sourcePage, sourcePlacement])

  return (
    <div
      ref={containerRef}
      className="relative my-8 overflow-hidden rounded-2xl p-6 sm:p-7"
      style={{
        background: 'linear-gradient(180deg, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.55) 100%)',
        backdropFilter: 'blur(16px) saturate(140%)',
        WebkitBackdropFilter: 'blur(16px) saturate(140%)',
        border: '1px solid rgba(255,255,255,0.65)',
        boxShadow: '0 12px 40px rgba(22,163,74,0.10), inset 0 1px 0 rgba(255,255,255,0.85)',
      }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(134,239,172,0.45) 0%, rgba(134,239,172,0) 70%)',
          filter: 'blur(12px)',
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-20 -left-16 h-56 w-56 rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(251,191,36,0.30) 0%, rgba(251,191,36,0) 70%)',
          filter: 'blur(12px)',
        }}
      />
      <div className="relative flex items-start gap-4">
        <div
          className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-[14px] text-green-600"
          style={{
            background: 'linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(240,253,244,0.75) 100%)',
            border: '1px solid rgba(255,255,255,0.8)',
            boxShadow: '0 6px 16px rgba(22,163,74,0.14), inset 0 1px 0 rgba(255,255,255,0.9)',
          }}
        >
          <ClipboardList className="h-6 w-6" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-lg font-bold tracking-tight text-green-800">{funnel.label} quotes</p>
          <p className="mt-2 text-[15px] leading-relaxed text-gray-600">
            Get matched with local {funnel.label.toLowerCase()} pros on Angi. Free quotes, no obligation.
          </p>
        </div>
      </div>
      <a
        href={href}
        target="_blank"
        rel="sponsored noopener noreferrer"
        onClick={() => trackEvent('service_lead_angi_clicked', { ...props, placement: sourcePlacement })}
        className="relative mt-5 flex min-h-[56px] w-full items-center justify-center gap-2.5 rounded-full px-6 text-base font-bold tracking-tight text-white transition-all duration-200 hover:-translate-y-px active:translate-y-0 active:scale-[0.985]"
        style={{
          background: 'linear-gradient(90deg, rgba(22,163,74,0.92) 0%, rgba(5,150,105,0.92) 100%)',
          border: '1px solid rgba(255,255,255,0.35)',
          boxShadow: '0 10px 24px -6px rgba(22,163,74,0.45), inset 0 1px 0 rgba(255,255,255,0.35)',
        }}
      >
        {funnel.ctaLabel}
        <ArrowRight className="h-5 w-5" aria-hidden="true" />
      </a>
      <p className="relative mt-3 text-center text-xs text-gray-500">
        Angi is a partner; we may earn a commission if you request quotes there.
      </p>
    </div>
  )
}
